import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { collection, doc, onSnapshot, query, setDoc, where } from 'firebase/firestore';
import type { GlobalAsset, Organization, SrmProject, UserProfile } from '@types';
import { db } from '../../firebaseConfig';
import { useGlobalAssets } from '../context/GlobalAssetContext';
import { countryFacilities, facilityFramework } from '../utils/countryFacilityFrameworks';
import SrmArchitectInput from './SrmArchitectInput';

const CountryFacilitiesCard: React.FC<{
    organization: Organization;
    iso3: string;
    countryName: string;
    currentUser: UserProfile;
    readOnly: boolean;
    onNavigate: (nav: { page: string; params?: any }) => void;
}> = ({ organization, iso3, countryName, currentUser, readOnly, onNavigate }) => {
    const { assets } = useGlobalAssets();
    const facilities = useMemo(() => countryFacilities(assets, iso3), [assets, iso3]);
    const canSetUp = !readOnly || ['owner', 'admin', 'editor'].includes(currentUser.role);
    const [projects, setProjects] = useState<SrmProject[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [retry, setRetry] = useState(0);
    const [selectedAsset, setSelectedAsset] = useState<GlobalAsset | null>(null);
    const [processing, setProcessing] = useState(false);
    const closeButton = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        setSelectedAsset(null);
        setProcessing(false);
    }, [organization.id, iso3]);

    useEffect(() => {
        setLoading(true);
        setError(false);
        setProjects([]);
        return onSnapshot(query(collection(db, 'srm_projects'), where('organizationId', '==', organization.id)), snapshot => {
            setProjects(snapshot.docs.map(project => ({ ...project.data(), id: project.id } as SrmProject)));
            setLoading(false);
        }, () => {
            setError(true);
            setLoading(false);
        });
    }, [organization.id, retry]);

    useEffect(() => {
        if (!selectedAsset)
            return;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        closeButton.current?.focus();
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && !processing)
                setSelectedAsset(null);
        };
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [selectedAsset, processing]);

    const closeModal = () => {
        if (!processing)
            setSelectedAsset(null);
    };

    const saveProject = async (project: SrmProject) => {
        await setDoc(doc(db, 'srm_projects', project.id), project);
    };

    return <>
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" aria-label={`${countryName} facilities and SRM frameworks`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="text-xl font-bold text-slate-900">Assets &amp; Facilities</h2>
                    <p className="mt-1 text-sm text-slate-600">Facility locations in {countryName}, from the Global Asset Manager.</p>
                </div>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">{facilities.length} {facilities.length === 1 ? 'facility' : 'facilities'}</span>
            </div>
            {error && <p role="alert" className="mt-4 text-sm text-red-700">Frameworks could not load. <button type="button" onClick={() => setRetry(value => value + 1)} className="font-bold underline">Retry</button></p>}
            {facilities.length ? <div className="mt-5 grid gap-3 lg:grid-cols-2">
                {facilities.map(asset => {
                    const framework = facilityFramework(projects, asset, organization.id, iso3);
                    return <article key={asset.id} className="flex flex-col justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:flex-row sm:items-center">
                        <div className="min-w-0">
                            <h3 className="font-bold text-slate-900">{asset.name}</h3>
                            <p className="mt-1 break-words text-sm text-slate-600">{asset.location?.address || asset.country || countryName}</p>
                            <p className="mt-1 text-xs text-slate-500">{asset.criticality} criticality{asset.isPrimary ? ' · Primary' : ''}</p>
                        </div>
                        {framework?.status === 'Compiling' || framework?.status === 'Architecting' ? <span className="text-xs font-semibold text-blue-700">Framework building…</span> : framework ? <button type="button" onClick={() => onNavigate({ page: 'srm_command_center', params: { projectId: framework.id, view: 'dashboard' } })}
                            className="shrink-0 rounded-lg bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-500">
                            Open Framework
                        </button> : canSetUp && !loading && !error ? <button type="button" onClick={() => setSelectedAsset(asset)}
                            className="shrink-0 rounded-lg bg-blue-700 px-4 py-2 text-sm font-bold text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500">
                            Set Up Framework
                        </button> : <span className="text-xs font-semibold text-slate-500">{loading ? 'Checking framework…' : error ? 'Framework unavailable' : 'Setup requires editor access'}</span>}
                    </article>;
                })}
            </div> : <p className="mt-5 text-sm text-slate-500">No facility assets exist for {countryName} yet.</p>}
        </section>
        {selectedAsset && createPortal(<div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/75 p-3 sm:p-6" onMouseDown={event => {
            if (event.target === event.currentTarget)
                closeModal();
        }}>
            <div role="dialog" aria-modal="true" aria-label={`SRM Auto-Architect for ${selectedAsset.name}`} className="max-h-full w-full max-w-5xl overflow-y-auto rounded-2xl bg-slate-50 shadow-2xl">
                <div className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-slate-200 bg-white px-5 py-3">
                    <p className="truncate text-sm font-bold text-slate-900">Framework for {selectedAsset.name}</p>
                    <button ref={closeButton} type="button" disabled={processing} onClick={closeModal} className="rounded-lg px-3 py-1 text-sm font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-50">Close</button>
                </div>
                <div className="p-4 sm:p-6">
                    <SrmArchitectInput key={selectedAsset.id} organization={organization} initialAsset={selectedAsset} onProcessingChange={setProcessing}
                        onSave={saveProject} onGenerate={project => {
                            setSelectedAsset(null);
                            onNavigate({ page: 'srm_command_center', params: { projectId: project.id, view: 'dashboard' } });
                        }} onViewProjects={closeModal} onCancel={closeModal} onNavigate={nav => {
                            setSelectedAsset(null);
                            if (nav.page !== 'organizationDashboard')
                                onNavigate(nav);
                        }} />
                </div>
            </div>
        </div>, document.body)}
    </>;
};

export default CountryFacilitiesCard;
